import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementConfirmEvent
} from '@stripe/stripe-js';
import { Box } from '@chakra-ui/react';

interface StripeExpressCheckoutElementProps {
  onConfirm?: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onReady?: () => void;
  onEscape?: () => void;
  onSuccess?: () => void;
  priceAmount?: number;
  recurring?: {
    interval?: string;
    intervalCount?: number;
  };
  useWideLayout?: boolean;
  isTrial?: boolean;
}

export default function StripeExpressCheckoutElement({
  onConfirm,
  onReady,
  onEscape,
  onSuccess,
  priceAmount,
  recurring,
  useWideLayout = false,
  isTrial = false
}: StripeExpressCheckoutElementProps) {
  const stripe = useStripe();
  const elements = useElements();

  // Don't render if we don't have a valid price amount (unless it's a trial with $0)
  if (!isTrial && (!priceAmount || priceAmount <= 0)) {
    return null;
  }

  // Don't render if Stripe isn't ready yet
  if (!stripe || !elements) {
    return null;
  }

  const expressCheckoutOptions: StripeExpressCheckoutElementOptions = {
    buttonHeight: 50,
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black',
    },
    buttonType: {
      googlePay: "buy",
      applePay: "buy"
    },
    paymentMethodOrder: ['apple_pay', 'google_pay'],
    layout: {
      maxColumns: useWideLayout ? 2 : 1,
      maxRows: useWideLayout ? 1 : 0, // 0 means no limit on rows
      overflow: useWideLayout ? 'auto' : 'never',
    },
    wallets: {
      applePay: 'always',
      googlePay: 'always',
    },
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    // Call parent onConfirm if provided - let parent handle all error scenarios
    if (onConfirm) {
      onConfirm(event);
    } else {
      // Default behavior - just resolve the payment
      // The parent component should handle the actual payment processing
      onSuccess?.();
    }
  };

  return (
    <Box
      width="100%"
      mb={4}
    >
      <ExpressCheckoutElement
        id="express-checkout-element"
        options={expressCheckoutOptions}
        onConfirm={handleConfirm}
        onReady={onReady}
        onEscape={onEscape}
        onClick={(event) => {
          event.resolve({
            emailRequired: true,
            billingAddressRequired: true,
            business: {
              name: 'Catch'
            }
          });
        }}
      />
    </Box>
  );
}
